@if (!hideLayout) {
  <lib-account-layout [menuItems]="accountMenuItems">
    <ng-container *ngTemplateOutlet="content"></ng-container>
  </lib-account-layout>
} @else {
  <div class="settings-standalone">
    <ng-container *ngTemplateOutlet="content"></ng-container>
  </div>
}

<ng-template #content>
  <lib-admin-page-shell
    [title]="'SETTINGS.TITLE' | translate"
    [subtitle]="'SETTINGS.SUBTITLE' | translate"
    extraClass="page-content-settings"
  >
    <ng-container adminPageTabs>
      @for (tab of settingsTabs; track tab.id) {
      <button
        type="button"
        class="admin-page-tab"
        role="tab"
        [attr.aria-selected]="activeTab() === tab.id"
        [class.active]="activeTab() === tab.id"
        (click)="setActiveTab(tab.id)"
      >
        {{ tab.labelKey | translate }}
      </button>
      }
    </ng-container>

  <div class="settings-container">
    @if (activeTab() === 'profile') {
    <div class="settings-profile-layout">
      <aside class="settings-profile-identity">
        <button
          type="button"
          class="settings-avatar"
          (click)="onOpenAvatarModal()"
          [attr.aria-label]="'SETTINGS.CHANGE_PHOTO' | translate"
        >
          <span class="settings-avatar-face">
            @if (userAvatarUrl()) {
            <img [src]="userAvatarUrl()" alt="" />
            } @else {
            <span>{{ userInitials() }}</span>
            }
          </span>
          <span class="settings-avatar-camera" aria-hidden="true">
            <svg lucideCamera [size]="13" [strokeWidth]="2.2"></svg>
          </span>
        </button>
        <div class="settings-identity-copy">
          <strong>{{ displayName() }}</strong>
          <span>{{ roleLabelKey() | translate }}</span>
          @if (memberSinceText()) {
          <span class="settings-identity-meta">
            {{ 'SETTINGS.MEMBER_SINCE' | translate }} {{ memberSinceText() }}
          </span>
          }
        </div>
      </aside>

      <div class="settings-profile-details">
        <header class="settings-section-head">
          <h2>{{ 'SETTINGS.PERSONAL_DETAILS' | translate }}</h2>
          <p>{{ 'SETTINGS.PERSONAL_DETAILS_DESC' | translate }}</p>
        </header>
        <div class="settings-form-grid">
          <label class="drawer-field">
            <span class="drawer-field-label">{{ 'SETTINGS.FIRST_NAME' | translate }}</span>
            <input
              class="drawer-control"
              type="text"
              [class.is-error]="isFirstNameInvalid()"
              [ngModel]="firstName()"
              (ngModelChange)="firstName.set($event)"
              (blur)="firstNameTouched.set(true)"
            />
            <span
              class="settings-field-error"
              [class.settings-field-error--hidden]="!isFirstNameInvalid()"
            >{{ 'SETTINGS.FIRST_NAME_REQUIRED' | translate }}</span>
          </label>
          <label class="drawer-field">
            <span class="drawer-field-label">{{ 'SETTINGS.LAST_NAME' | translate }}</span>
            <input
              class="drawer-control"
              type="text"
              [class.is-error]="isLastNameInvalid()"
              [ngModel]="lastName()"
              (ngModelChange)="lastName.set($event)"
              (blur)="lastNameTouched.set(true)"
            />
            <span
              class="settings-field-error"
              [class.settings-field-error--hidden]="!isLastNameInvalid()"
            >{{ 'SETTINGS.LAST_NAME_REQUIRED' | translate }}</span>
          </label>
          <label class="drawer-field drawer-field--full">
            <span class="drawer-field-label">{{ 'SETTINGS.EMAIL' | translate }}</span>
            <span class="settings-email-lock">
              <input
                class="drawer-control"
                type="email"
                [value]="email()"
                disabled
                readonly
                tabindex="-1"
                autocomplete="off"
              />
            </span>
          </label>
        </div>
        <div class="settings-action-row">
          <button
            type="button"
            class="button button-primary settings-action-button"
            [disabled]="isProfileSaving() || !canSaveProfile()"
            (click)="onSaveProfile()"
          >
            {{ 'SETTINGS.SAVE_CHANGES' | translate }}
          </button>
        </div>
      </div>
    </div>
    }

    @if (activeTab() === 'security') {
    <div class="settings-workspace">
      <section class="settings-pane">
        <header class="settings-pane-aside">
          <h2>{{ 'SETTINGS.PASSWORD' | translate }}</h2>
          <p>{{ 'SETTINGS.PASSWORD_DESC' | translate }}</p>
        </header>
        <div class="settings-pane-main">
          <div class="settings-form-stack">
            <div class="settings-security-form">
              <div class="drawer-field">
                <label class="drawer-field-label" for="settings-current-password">{{ 'SETTINGS.CURRENT_PASSWORD' | translate }}</label>
                <span class="settings-password-field">
                  <input
                    id="settings-current-password"
                    class="drawer-control"
                    [type]="showCurrentPassword() ? 'text' : 'password'"
                    autocomplete="current-password"
                    [ngModel]="currentPassword()"
                    (ngModelChange)="currentPassword.set($event)"
                  />
                  <button
                    type="button"
                    class="settings-password-toggle"
                    (click)="showCurrentPassword.set(!showCurrentPassword())"
                    [attr.aria-label]="(showCurrentPassword() ? 'SETTINGS.HIDE_PASSWORD' : 'SETTINGS.SHOW_PASSWORD') | translate"
                    [attr.aria-pressed]="showCurrentPassword()"
                  >
                    @if (showCurrentPassword()) {
                    <svg lucideEyeOff [size]="15" [strokeWidth]="2" aria-hidden="true"></svg>
                    } @else {
                    <svg lucideEye [size]="15" [strokeWidth]="2" aria-hidden="true"></svg>
                    }
                  </button>
                </span>
              </div>
              <div class="drawer-field">
                <label class="drawer-field-label" for="settings-new-password">{{ 'SETTINGS.NEW_PASSWORD' | translate }}</label>
                <span class="settings-password-field">
                  <input
                    id="settings-new-password"
                    class="drawer-control"
                    [type]="showNewPassword() ? 'text' : 'password'"
                    autocomplete="new-password"
                    [class.is-error]="isNewPasswordInvalid()"
                    [ngModel]="newPassword()"
                    (ngModelChange)="newPassword.set($event); checkPasswordRequirements($event)"
                  />
                  <button
                    type="button"
                    class="settings-password-toggle"
                    (click)="showNewPassword.set(!showNewPassword())"
                    [attr.aria-label]="(showNewPassword() ? 'SETTINGS.HIDE_PASSWORD' : 'SETTINGS.SHOW_PASSWORD') | translate"
                    [attr.aria-pressed]="showNewPassword()"
                  >
                    @if (showNewPassword()) {
                    <svg lucideEyeOff [size]="15" [strokeWidth]="2" aria-hidden="true"></svg>
                    } @else {
                    <svg lucideEye [size]="15" [strokeWidth]="2" aria-hidden="true"></svg>
                    }
                  </button>
                </span>
              </div>
              <div class="drawer-field">
                <label class="drawer-field-label" for="settings-confirm-password">{{ 'SETTINGS.CONFIRM_PASSWORD' | translate }}</label>
                <span class="settings-password-field">
                  <input
                    id="settings-confirm-password"
                    class="drawer-control"
                    [type]="showConfirmPassword() ? 'text' : 'password'"
                    autocomplete="new-password"
                    [class.is-error]="isConfirmPasswordInvalid()"
                    [ngModel]="confirmPassword()"
                    (ngModelChange)="confirmPassword.set($event)"
                  />
                  <button
                    type="button"
                    class="settings-password-toggle"
                    (click)="showConfirmPassword.set(!showConfirmPassword())"
                    [attr.aria-label]="(showConfirmPassword() ? 'SETTINGS.HIDE_PASSWORD' : 'SETTINGS.SHOW_PASSWORD') | translate"
                    [attr.aria-pressed]="showConfirmPassword()"
                  >
                    @if (showConfirmPassword()) {
                    <svg lucideEyeOff [size]="15" [strokeWidth]="2" aria-hidden="true"></svg>
                    } @else {
                    <svg lucideEye [size]="15" [strokeWidth]="2" aria-hidden="true"></svg>
                    }
                  </button>
                </span>
              </div>
            </div>

            @if (newPassword() && !arePasswordRequirementsMet()) {
            <div class="password-requirements">
              <p class="requirements-title">
                {{ 'SETTINGS.PASSWORD_REQUIREMENTS' | translate }}:
              </p>
              <ul class="requirements-list">
                <li
                  [class.valid]="passwordRequirements.minLength()"
                  [class.invalid]="!passwordRequirements.minLength()"
                >
                  <span class="requirement-icon">{{
                    passwordRequirements.minLength() ? '✓' : '✗'
                  }}</span>
                  {{ 'SETTINGS.REQ_MIN_LENGTH' | translate }}
                </li>
                <li
                  [class.valid]="passwordRequirements.hasUppercase()"
                  [class.invalid]="!passwordRequirements.hasUppercase()"
                >
                  <span class="requirement-icon">{{
                    passwordRequirements.hasUppercase() ? '✓' : '✗'
                  }}</span>
                  {{ 'SETTINGS.REQ_UPPERCASE' | translate }}
                </li>
                <li
                  [class.valid]="passwordRequirements.hasLowercase()"
                  [class.invalid]="!passwordRequirements.hasLowercase()"
                >
                  <span class="requirement-icon">{{
                    passwordRequirements.hasLowercase() ? '✓' : '✗'
                  }}</span>
                  {{ 'SETTINGS.REQ_LOWERCASE' | translate }}
                </li>
                <li
                  [class.valid]="passwordRequirements.hasNumber()"
                  [class.invalid]="!passwordRequirements.hasNumber()"
                >
                  <span class="requirement-icon">{{
                    passwordRequirements.hasNumber() ? '✓' : '✗'
                  }}</span>
                  {{ 'SETTINGS.REQ_NUMBER' | translate }}
                </li>
                <li
                  [class.valid]="passwordRequirements.hasSpecialChar()"
                  [class.invalid]="!passwordRequirements.hasSpecialChar()"
                >
                  <span class="requirement-icon">{{
                    passwordRequirements.hasSpecialChar() ? '✓' : '✗'
                  }}</span>
                  {{ 'SETTINGS.REQ_SPECIAL_CHAR' | translate }}
                </li>
              </ul>
            </div>
            }

            @if (confirmPassword()) {
            @if (doPasswordsMatch()) {
            <span class="password-match-message">
              <span class="match-icon">✓</span>
              {{ 'SETTINGS.PASSWORDS_MATCH' | translate }}
            </span>
            } @else {
            <span class="password-mismatch-message">
              <span class="mismatch-icon">✗</span>
              {{ 'SETTINGS.PASSWORDS_DO_NOT_MATCH' | translate }}
            </span>
            }
            }

            <div class="settings-action-row">
              <button
                type="button"
                class="button button-primary settings-action-button"
                [disabled]="isPasswordChanging()"
                (click)="onChangePassword()"
              >
                {{ 'SETTINGS.UPDATE_PASSWORD' | translate }}
              </button>
            </div>
          </div>
        </div>
      </section>
    </div>
    }

    @if (activeTab() === 'preferences') {
    <div class="settings-workspace">
      <section class="settings-pane">
        <header class="settings-pane-aside">
          <h2>{{ 'SETTINGS.REGION_FORMATS' | translate }}</h2>
          <p>{{ 'SETTINGS.REGION_FORMATS_DESC' | translate }}</p>
        </header>
        <div class="settings-pane-main">
          <div class="settings-form-grid">
            <label class="drawer-field">
              <span class="drawer-field-label">{{ 'SETTINGS.LANGUAGE' | translate }}</span>
              <select
                class="drawer-control"
                [ngModel]="selectedLanguage()"
                (ngModelChange)="onLanguageChange($event)"
              >
                @for (opt of languageOptions(); track opt.value) {
                <option [value]="opt.value">{{ opt.label }}</option>
                }
              </select>
            </label>
            <div class="drawer-field tz-picker" (click)="$event.stopPropagation()">
              <span class="drawer-field-label">{{ 'SETTINGS.TIMEZONE' | translate }}</span>
              <button
                type="button"
                class="drawer-control tz-picker-trigger"
                [attr.aria-expanded]="isTimezoneMenuOpen()"
                (click)="toggleTimezoneMenu()"
              >
                <span>{{ selectedTimezoneLabel() }}</span>
                <svg lucideChevronDown [size]="16" [strokeWidth]="2" aria-hidden="true"></svg>
              </button>
              @if (isTimezoneMenuOpen()) {
              <div class="tz-picker-panel" role="listbox">
                <label class="tz-picker-search">
                  <svg lucideSearch [size]="15" [strokeWidth]="2" aria-hidden="true"></svg>
                  <input
                    type="search"
                    [ngModel]="timezoneQuery()"
                    (ngModelChange)="timezoneQuery.set($event)"
                    [attr.placeholder]="'SETTINGS.SEARCH_TIME_ZONES' | translate"
                    [attr.aria-label]="'SETTINGS.SEARCH_TIME_ZONES' | translate"
                  />
                </label>
                <div class="tz-picker-list">
                  @for (zone of filteredTimezones(); track zone.value) {
                  <button
                    type="button"
                    role="option"
                    class="tz-picker-option"
                    [class.is-selected]="selectedTimezone() === zone.value"
                    [attr.aria-selected]="selectedTimezone() === zone.value"
                    (click)="selectTimezone(zone.value)"
                  >
                    <span>{{ zone.city }}</span>
                    <span>{{ zone.offset }}</span>
                  </button>
                  }
                </div>
              </div>
              }
            </div>
            <label class="drawer-field">
              <span class="drawer-field-label">{{ 'SETTINGS.DATE_FORMAT' | translate }}</span>
              <select
                class="drawer-control"
                [ngModel]="selectedDateFormat()"
                (ngModelChange)="selectedDateFormat.set($event)"
              >
                @for (opt of dateFormatOptions(); track opt.value) {
                <option [value]="opt.value">{{ opt.label }}</option>
                }
              </select>
            </label>
            <label class="drawer-field">
              <span class="drawer-field-label">{{ 'SETTINGS.TIME_FORMAT' | translate }}</span>
              <select
                class="drawer-control"
                [ngModel]="selectedTimeFormat()"
                (ngModelChange)="selectedTimeFormat.set($event)"
              >
                @for (opt of timeFormatOptions(); track opt.value) {
                <option [value]="opt.value">{{ opt.label }}</option>
                }
              </select>
            </label>
          </div>
        </div>
      </section>

      <section class="settings-pane">
        <header class="settings-pane-aside">
          <h2>{{ 'SETTINGS.BAR_DESIGNER_CONTROLS' | translate }}</h2>
          <p>{{ 'SETTINGS.BAR_DESIGNER_CONTROLS_DESC' | translate }}</p>
        </header>
        <div class="settings-pane-main">
          <div class="settings-form-grid">
            <label class="drawer-field">
              <span class="drawer-field-label">{{ KEY_MOUSE_CONTROLS | translate }}</span>
              <select
                class="drawer-control"
                [ngModel]="selectedMouseControl()"
                (ngModelChange)="onMouseControlChange($event)"
              >
                @for (opt of mouseControlOptions; track opt.value) {
                <option [ngValue]="opt.value">{{ opt.labelKey | translate }}</option>
                }
              </select>
            </label>
          </div>
        </div>
      </section>

      <section class="settings-pane">
        <header class="settings-pane-aside">
          <h2>{{ 'SETTINGS.LAB_APPLICATION_DEFAULTS' | translate }}</h2>
          <p>{{ 'SETTINGS.LAB_APPLICATION_DEFAULTS_DESC' | translate }}</p>
        </header>
        <div class="settings-pane-main">
          <div class="settings-form-grid settings-form-grid--single">
            <label class="drawer-field">
              <span class="drawer-field-label">{{ 'SETTINGS.TOOTH_NOTATION' | translate }}</span>
              <select
                class="drawer-control"
                [ngModel]="selectedToothNotation()"
                (ngModelChange)="selectedToothNotation.set($event)"
              >
                @for (opt of toothNotationOptions(); track opt.value) {
                <option [value]="opt.value">{{ opt.label }}</option>
                }
              </select>
            </label>
            <label class="drawer-field">
              <span class="drawer-field-label">{{ 'SETTINGS.DEFAULT_FILE_FORMAT' | translate }}</span>
              <select
                class="drawer-control"
                [ngModel]="selectedFileFormat()"
                (ngModelChange)="selectedFileFormat.set($event)"
              >
                @for (opt of fileFormatOptions(); track opt.value) {
                <option [value]="opt.value">{{ opt.label }}</option>
                }
              </select>
            </label>
          </div>
          <div class="settings-action-row">
            <button
              type="button"
              class="button button-primary settings-action-button"
              [disabled]="isPreferencesSaving()"
              (click)="onSavePreferences()"
            >
              {{ 'SETTINGS.SAVE_CHANGES' | translate }}
            </button>
          </div>
        </div>
      </section>
    </div>
    }
  </div>
  </lib-admin-page-shell>

  <!-- Avatar Upload Dialog -->
  @if (isAvatarModalOpen()) {
    <lib-profile-avatar-dialog
      mode="user"
      (closeDialog)="onCloseAvatarModal()"
      (saved)="onAvatarSaved()"
    />
  }
</ng-template>
